/**
 * Copyright 2025 Amazon.com, Inc. and its affiliates. All Rights Reserved.
 *
 * Licensed under the Amazon Software License (the "License").
 * You may not use this file except in compliance with the License.
 * A copy of the License is located at
 *
 *   http://aws.amazon.com/asl/
 *
 * or in the "license" file accompanying this file. This file is distributed
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the License for the specific language governing
 * permissions and limitations under the License.
 */

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts';
import { Spinner, Container, Box } from '@cloudscape-design/components';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that ensures user is authenticated before rendering children
 * Redirects to login if not authenticated
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // If not loading and not authenticated, redirect to login
    if (!isLoading && !isAuthenticated) {
      // Save the current location to redirect back after login
      sessionStorage.setItem('redirectAfterLogin', location.pathname + location.search);
      login();
    }
  }, [isLoading, isAuthenticated, login, location]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 'm' }}>
            Checking authentication...
          </Box>
        </Box>
      </Container>
    );
  }

  // If not authenticated, show loading while redirecting
  if (!isAuthenticated) {
    return (
      <Container>
        <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
          <Spinner size="large" />
          <Box variant="p" padding={{ top: 'm' }}>
            Redirecting to login...
          </Box>
        </Box>
      </Container>
    );
  }

  // User is authenticated, render children
  return <>{children}</>;
};
